import { createContext, useContext } from "react";
import type { NavigateFunction } from "react-router-dom";
import { basketsV3Service } from "../../../services/basketsV3Service";
import { portfoliosV3Service } from "../../../services/portfoliosV3Service";
import type { ProductShare } from "../../../services/productShareService";
import { productShareService } from "../../../services/productShareService";
import type { PerformanceResponse, PerformanceProductType } from "../../../services/productPerformanceService";
import { productPerformanceService } from "../../../services/productPerformanceService";

export type BasketRuntimeService = Pick<
  typeof basketsV3Service,
  | "createBasket"
  | "getBaskets"
  | "getBasket"
  | "getContractStates"
  | "getAfterActivation"
  | "getActiveComposition"
  | "getChanges"
  | "getPendingContracts"
  | "cancelPendingActivations"
  | "getLockStatus"
  | "getPortfolioUsage"
  | "updateComposition"
  | "publishBasket"
  | "patchBasket"
  | "deleteBasket"
  | "getVersions"
  | "createVersionMetadata"
  | "getVersionMetadata"
  | "deleteVersionMetadata"
  | "getAllVersionMetadata"
>;

export type PortfolioRuntimeService = Pick<
  typeof portfoliosV3Service,
  | "createPortfolio"
  | "getPortfolio"
  | "getContractStates"
  | "getBasketStates"
  | "getAfterActivation"
  | "getActiveComposition"
  | "getChanges"
  | "getPendingContracts"
  | "cancelPendingActivations"
  | "getLockStatus"
  | "getAuditLog"
  | "updateComposition"
  | "publishPortfolio"
  | "patchPortfolio"
  | "deletePortfolio"
  | "getVersions"
  | "createVersionMetadata"
  | "getVersionMetadata"
  | "deleteVersionMetadata"
  | "getAllVersionMetadata"
>;

export interface ShareLookupService {
  getShare(
    productType: "BASKET" | "PORTFOLIO",
    productId: number
  ): Promise<ProductShare | null>;
}

export type PerformanceFetcher = (
  productType: PerformanceProductType,
  productId: number
) => Promise<PerformanceResponse>;

export type CurationPerformanceChartDataSource = "v3-factors" | "v3-factors-admin";

export interface CurationRuntime {
  scope: "provider" | "admin";
  providerId: number | null;
  basketService: BasketRuntimeService;
  portfolioService: PortfolioRuntimeService;
  shareService: ShareLookupService;
  showPerformanceTile: boolean;
  performanceChartDataSource: CurationPerformanceChartDataSource;
  getPerformance: PerformanceFetcher;
  getBackPath: (productType: "basket" | "portfolio") => string;
  getBasketEditPath: (basketId: number) => string;
  afterPublishSuccess: (args: {
    productType: "basket" | "portfolio";
    navigate: NavigateFunction;
    reload: () => Promise<void>;
  }) => Promise<void>;
  afterDeleteSuccess: (args: {
    productType: "basket" | "portfolio";
    navigate: NavigateFunction;
  }) => void;
}

const providerRuntime: CurationRuntime = {
  scope: "provider",
  providerId: null,
  basketService: basketsV3Service,
  portfolioService: portfoliosV3Service,
  shareService: productShareService,
  showPerformanceTile: true,
  performanceChartDataSource: "v3-factors",
  getPerformance: (productType, productId) =>
    productPerformanceService.getProviderPerformance(productType, productId),
  getBackPath: (pt) =>
    pt === "basket" ? "/provider/baskets" : "/provider/portfolios",
  getBasketEditPath: (basketId) => `/provider/baskets/edit/${basketId}`,
  afterPublishSuccess: async ({ reload }) => {
    await reload();
  },
  afterDeleteSuccess: ({ productType, navigate }) => {
    navigate(
      productType === "basket" ? "/provider/baskets" : "/provider/portfolios"
    );
  },
};

export const CurationRuntimeContext =
  createContext<CurationRuntime>(providerRuntime);

export function useCurationRuntime(): CurationRuntime {
  return useContext(CurationRuntimeContext);
}
